import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const Analytics = () => {
  const [dailyStats, setDailyStats] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [dayOfWeekStats, setDayOfWeekStats] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('daily');
  const { fetchWithAuth } = useAuth();

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [daily, weekly, monthly, dayOfWeek, trendsData] = await Promise.all([
        fetchWithAuth('/api/analytics/daily?days=30').then(r => r.json()),
        fetchWithAuth('/api/analytics/weekly?weeks=12').then(r => r.json()),
        fetchWithAuth('/api/analytics/monthly?months=12').then(r => r.json()),
        fetchWithAuth('/api/analytics/by-day-of-week?days=90').then(r => r.json()),
        fetchWithAuth('/api/analytics/trends').then(r => r.json())
      ]);
      
      setDailyStats(daily);
      setWeeklyStats(weekly);
      setMonthlyStats(monthly);
      setDayOfWeekStats(dayOfWeek);
      setTrends(trendsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num) => {
    return Math.round(num).toLocaleString();
  };

  if (loading) return <div className="loading">Loading analytics...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>
        Food Logging Analytics
      </h1>

      {/* Trend Cards */}
      {trends && (
        <div className="grid grid-4" style={{ marginBottom: '24px' }}>
          <div className="stat-card">
            <div className="stat-label">This Week</div>
            <div className="stat-value" style={{ fontSize: '28px' }}>{trends.thisWeek}</div>
            <div style={{ 
              color: trends.weekChange >= 0 ? '#10b981' : '#ef4444',
              fontSize: '14px',
              fontWeight: '600',
              marginTop: '8px'
            }}>
              {trends.weekChange >= 0 ? '↑' : '↓'} {Math.abs(trends.weekChange)}% vs last week
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Last Week</div>
            <div className="stat-value" style={{ fontSize: '28px', color: '#6b7280' }}>
              {trends.lastWeek}
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
              Previous period
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-label">This Month</div>
            <div className="stat-value" style={{ fontSize: '28px' }}>{trends.thisMonth}</div>
            <div style={{ 
              color: trends.monthChange >= 0 ? '#10b981' : '#ef4444',
              fontSize: '14px',
              fontWeight: '600',
              marginTop: '8px'
            }}>
              {trends.monthChange >= 0 ? '↑' : '↓'} {Math.abs(trends.monthChange)}% vs last month
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Last Month</div>
            <div className="stat-value" style={{ fontSize: '28px', color: '#6b7280' }}>
              {trends.lastMonth}
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
              Previous period
            </div>
          </div>
        </div>
      )}

      {/* View Selector */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '8px' }}>
        <button
          className={`btn ${view === 'daily' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setView('daily')}
        >
          Daily
        </button>
        <button
          className={`btn ${view === 'weekly' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setView('weekly')}
        >
          Weekly
        </button>
        <button
          className={`btn ${view === 'monthly' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setView('monthly')}
        >
          Monthly
        </button>
        <button
          className={`btn ${view === 'dayofweek' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setView('dayofweek')}
        >
          By Day of Week
        </button>
      </div>

      {/* Daily Stats */}
      {view === 'daily' && (
        <div className="card">
          <h2 className="card-title">Daily Food Logs (Last 30 Days)</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Food Logs</th>
                  <th>Total Calories</th>
                  <th>Avg Calories/Log</th>
                </tr>
              </thead>
              <tbody>
                {dailyStats.map((stat, index) => (
                  <tr key={index}>
                    <td><strong>{formatDate(stat.date)}</strong></td>
                    <td>{formatNumber(stat.count)}</td>
                    <td>{formatNumber(stat.total_calories)} kcal</td>
                    <td>{formatNumber(stat.avg_calories)} kcal</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Weekly Stats */}
      {view === 'weekly' && (
        <div className="card">
          <h2 className="card-title">Weekly Food Logs (Last 12 Weeks)</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Week Starting</th>
                  <th>Week #</th>
                  <th>Food Logs</th>
                  <th>Total Calories</th>
                  <th>Avg Calories/Log</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {weeklyStats.map((stat, index) => {
                  const prevWeek = weeklyStats[index + 1];
                  const change = prevWeek 
                    ? ((stat.count - prevWeek.count) / prevWeek.count * 100).toFixed(1)
                    : null;
                  
                  return (
                    <tr key={index}>
                      <td><strong>{formatDate(stat.week_start)}</strong></td>
                      <td>Week {stat.week}, {stat.year}</td>
                      <td>{formatNumber(stat.count)}</td>
                      <td>{formatNumber(stat.total_calories)} kcal</td>
                      <td>{formatNumber(stat.avg_calories)} kcal</td>
                      <td>
                        {change !== null && (
                          <span style={{ 
                            color: change >= 0 ? '#10b981' : '#ef4444',
                            fontWeight: '600'
                          }}>
                            {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Stats */}
      {view === 'monthly' && (
        <div className="card">
          <h2 className="card-title">Monthly Food Logs (Last 12 Months)</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Food Logs</th>
                  <th>Total Calories</th>
                  <th>Avg Calories/Log</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {monthlyStats.map((stat, index) => {
                  const prevMonth = monthlyStats[index + 1];
                  const change = prevMonth 
                    ? ((stat.count - prevMonth.count) / prevMonth.count * 100).toFixed(1)
                    : null;
                  
                  const monthName = new Date(stat.month_start).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long'
                  });
                  
                  return (
                    <tr key={index}>
                      <td><strong>{monthName}</strong></td>
                      <td>{formatNumber(stat.count)}</td>
                      <td>{formatNumber(stat.total_calories)} kcal</td>
                      <td>{formatNumber(stat.avg_calories)} kcal</td>
                      <td>
                        {change !== null && (
                          <span style={{ 
                            color: change >= 0 ? '#10b981' : '#ef4444',
                            fontWeight: '600'
                          }}>
                            {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Day of Week Stats */}
      {view === 'dayofweek' && (
        <div className="card">
          <h2 className="card-title">Food Logs by Day of Week (Last 90 Days)</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Total Food Logs</th>
                  <th>Avg Calories</th>
                  <th>Activity Level</th>
                </tr>
              </thead>
              <tbody>
                {dayOfWeekStats.map((stat, index) => {
                  const maxCount = Math.max(...dayOfWeekStats.map(s => s.count));
                  const percentage = (stat.count / maxCount * 100).toFixed(0);
                  
                  return (
                    <tr key={index}>
                      <td><strong>{stat.day_name}</strong></td>
                      <td>{formatNumber(stat.count)}</td>
                      <td>{formatNumber(stat.avg_calories)} kcal</td>
                      <td>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px' 
                        }}>
                          <div style={{
                            width: '100px',
                            height: '8px',
                            background: '#e5e7eb',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${percentage}%`,
                              height: '100%',
                              background: '#3b82f6',
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            {percentage}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;


